import { SidebarMenuButton, SidebarMenuItem } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/shared/lib';

type Props = {
  /** Адрес, на который ведёт пункт */
  path: string;
  /** Иконка слева от подписи */
  icon: ReactNode;
  /** Подпись пункта */
  label: ReactNode;
  /** Пункт отмечен активным; по умолчанию — когда открыт его адрес или вложенный в него */
  isActive?: boolean;
  /** Содержимое в конце строки: бейдж, счётчик и т.п. */
  endSlot?: ReactNode;
  /** Подсказка при наведении: нужна пунктам, чья подпись приходит из данных и может не поместиться */
  tooltip?: ReactNode;
  className?: string;
};

/**
 * Пункт бокового меню: иконка, подпись и переход по адресу.
 *
 * Единственное место, где задано оформление пунктов сайдбара и сопоставление «адрес → активный
 * пункт», — иначе и правка вида, и правило активности расходятся по статическому меню и
 * динамическим подменю.
 */
export const SidebarNavItem: FC<Props> = ({ path, icon, label, isActive, endSlot, tooltip, className }) => {
  const { pathname } = useLocation();

  // Раздел остаётся активным на своих вложенных адресах: открытая из него запись — тот же раздел.
  const isPathActive = pathname === path || pathname.startsWith(`${path}/`);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={tooltip}
        className={cn(
          'text-sidebar-foreground',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'active:bg-sidebar-accent active:text-sidebar-accent-foreground',
          'rounded-md py-2 px-2.5',
          'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
          className,
        )}
        isActive={isActive ?? isPathActive}
      >
        <NavLink to={path} className="flex items-center gap-2 w-full">
          {icon}
          {/* Подпись усекается здесь, а не у потребителей: ширина меню принадлежит раскладке,
              и пункт с длинным названием из данных не вправе её менять. */}
          <span className="truncate">{label}</span>
          {endSlot}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
